import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
    fetchAllergyActionPlans,
    selectPlans,
    selectMeta,
    selectLoading,
    selectError
} from '../../store/allergyActionPlan/allergyActionPlanSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const AllergyActionPlanListComp = ({ data, viewMode }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const plans = useSelector(selectPlans);
    const meta = useSelector(selectMeta);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const [currentPage, setCurrentPage] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    useEffect(() => {
        dispatch(fetchAllergyActionPlans({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
    }, [dispatch, currentPage]);

    const handleViewDetails = (plan) => {
        setSelectedPlan(plan);
        setShowDetailModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailModal(false);
        setSelectedPlan(null);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === '1899-11-29') return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Defensive check and definition for displayData
    const filteredData = viewMode === 'profile' ? (Array.isArray(data) ? data.filter(item => item.status === 1) : []) : [];
    const displayData = viewMode === 'profile' ? filteredData : (Array.isArray(plans) ? plans : []);
    if (viewMode === 'profile' && (!filteredData || filteredData.length === 0)) {
        return (
            <Container>
                <div className="text-center p-5">
                    <p className="text-muted">No active Allergy Action Plan. Please add new data.</p>
                    <Link to="/dashboard/allergy-action-plan/add">
                        <Button variant="primary">Create Your First Plan</Button>
                    </Link>
                </div>
            </Container>
        );
    }
    return (
        <Container>
            <Row>
                <Col sm="12">
                    {/* <Card> ...existing code... */}
                    {loading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="text-center p-5">
                            <p className="text-muted">No allergy action plans available</p>
                            <Link to="/dashboard/allergy-action-plan/add">
                                <Button variant="primary">Create Your First Plan</Button>
                            </Link>
                        </div>
                    ) : (
                        <>
                            <Row>
                                {/* If viewMode is not 'profile', use plans from Redux, else use filteredData from props */}
                                {/* Define displayData before using it */}
                                {displayData.map((plan) => (
                                            <Col lg="12" key={plan.id} className="mb-3">
                                                <Card className="shadow-sm">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <h5 className="mb-2">
                                                                    Action Plan #{plan.id}
                                                                    {plan.status === 1 ? (
                                                                        <Badge bg="success" className="ms-2">Active</Badge>
                                                                    ) : (
                                                                        <Badge bg="secondary" className="ms-2">Inactive</Badge>
                                                                    )}
                                                                </h5>
                                                                <div className="mb-2">
                                                                    {plan.is_peanuts === 1 && <Badge bg="warning" className="me-1">Peanuts</Badge>}
                                                                    {plan.is_shellfish === 1 && <Badge bg="warning" className="me-1">Shellfish</Badge>}
                                                                    {plan.is_tree_nuts === 1 && <Badge bg="warning" className="me-1">Tree Nuts</Badge>}
                                                                    {plan.is_fish === 1 && <Badge bg="warning" className="me-1">Fish</Badge>}
                                                                    {plan.is_milk === 1 && <Badge bg="warning" className="me-1">Milk</Badge>}
                                                                    {plan.is_food_additives === 1 && <Badge bg="warning" className="me-1">Food Additives</Badge>}
                                                                    {plan.require_epi_pen === 1 && <Badge bg="danger" className="me-1">EpiPen Required</Badge>}
                                                                </div>
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="info"
                                                                    size="sm"
                                                                    onClick={() => handleViewDetails(plan)}
                                                                >
                                                                    <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>visibility</i>
                                                                    View
                                                                </Button>
                                                                {plan.status === 1 && (
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        onClick={() => navigate(`/dashboard/allergy-action-plan/edit/${plan.id}`)}
                                                                    >
                                                                        <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>edit</i>
                                                                        Edit
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <Row>
                                                            <Col md="6">
                                                                <p className="mb-2">
                                                                    <strong>Symptoms:</strong>
                                                                    <br />
                                                                    {plan.symptoms || 'N/A'}
                                                                </p>
                                                                <p className="mb-2">
                                                                    <strong>Emergency Treatment:</strong>
                                                                    <br />
                                                                    {plan.emergency_treatment_procedures || 'N/A'}
                                                                </p>
                                                            </Col>
                                                            <Col md="6">
                                                                <div className="mb-3">
                                                                    <p className="mb-2"><strong>Parent Signature:</strong></p>
                                                                    {plan.parent_signature ? (
                                                                        <div className="border rounded p-2 bg-light mb-1">
                                                                            <img
                                                                                src={plan.parent_signature}
                                                                                alt="Parent Signature"
                                                                                style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted">N/A</span>
                                                                    )}
                                                                    <small className="text-muted">Date: {formatDate(plan.parent_signature_date)}</small>
                                                                </div>
                                                                {plan.supervisor_signature && (
                                                                    <div className="mb-2">
                                                                        <p className="mb-2"><strong>Supervisor Signature:</strong></p>
                                                                        <div className="border rounded p-2 bg-light mb-1">
                                                                            <img
                                                                                src={plan.supervisor_signature}
                                                                                alt="Supervisor Signature"
                                                                                style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                                                                            />
                                                                        </div>
                                                                        <small className="text-muted">Date: {formatDate(plan.supervisor_signature_date)}</small>
                                                                    </div>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>

                                    {/* Pagination */}
                                    {meta && meta.last_page > 1 && (
                                        <div className="d-flex justify-content-center mt-4">
                                            <nav>
                                                <ul className="pagination">
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(meta.last_page)].map((_, index) => (
                                                        <li
                                                            key={index + 1}
                                                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                                        >
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(index + 1)}
                                                            >
                                                                {index + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(currentPage + 1)}
                                                            disabled={currentPage === meta.last_page}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </>
                            )}
                        {/* </Card.Body> ...existing code... */}
                </Col>
            </Row>

            {/* Detail Modal */}
            <Modal
                show={showDetailModal}
                onHide={handleCloseModal}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Allergy Action Plan Details
                        {selectedPlan && (
                            selectedPlan.status === 1 ? (
                                <Badge bg="success" className="ms-2">Active</Badge>
                            ) : (
                                <Badge bg="secondary" className="ms-2">Inactive</Badge>
                            )
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedPlan && (
                        <div>
                            <h5 className="mb-3">Allergies:</h5>
                            <Row className="mb-3">
                                <Col md="6">
                                    <p><strong>Peanuts:</strong> {selectedPlan.is_peanuts === 1 ? 'Yes' : 'No'}</p>
                                    <p><strong>Shellfish:</strong> {selectedPlan.is_shellfish === 1 ? 'Yes' : 'No'}
                                        {selectedPlan.shellfish_specify && <><br /><small>({selectedPlan.shellfish_specify})</small></>}
                                    </p>
                                    <p><strong>Tree Nuts:</strong> {selectedPlan.is_tree_nuts === 1 ? 'Yes' : 'No'}</p>
                                    <p><strong>Fish:</strong> {selectedPlan.is_fish === 1 ? 'Yes' : 'No'}
                                        {selectedPlan.fish_specify && <><br /><small>({selectedPlan.fish_specify})</small></>}
                                    </p>
                                </Col>
                                <Col md="6">
                                    <p><strong>Milk:</strong> {selectedPlan.is_milk === 1 ? 'Yes' : 'No'}</p>
                                    <p><strong>Food Additives:</strong> {selectedPlan.is_food_additives === 1 ? 'Yes' : 'No'}
                                        {selectedPlan.food_additives_specify && <><br /><small>({selectedPlan.food_additives_specify})</small></>}
                                    </p>
                                    <p><strong>All Dairy Products:</strong> {selectedPlan.all_dairy_products === 1 ? 'Yes' : 'No'}</p>
                                    <p><strong>Other:</strong> {selectedPlan.is_other === 1 ? 'Yes' : 'No'}
                                        {selectedPlan.other_specify && <><br /><small>({selectedPlan.other_specify})</small></>}
                                    </p>
                                </Col>
                            </Row>

                            <hr />

                            <h5 className="mb-3">Insect Stings/Bites:</h5>
                            <p><strong>Has allergy:</strong> {selectedPlan.insect_stings_bites === 1 ? 'Yes' : 'No'}</p>
                            {selectedPlan.insect_stings_bites === 1 && (
                                <>
                                    <p><strong>Specify:</strong> {selectedPlan.insect_stings_bites_specify || 'N/A'}</p>
                                    <p><strong>Medication:</strong> {selectedPlan.insect_stings_bites_medication || 'N/A'}</p>
                                </>
                            )}

                            <hr />

                            <h5 className="mb-3">Symptoms & Treatment:</h5>
                            <p><strong>Symptoms:</strong><br />{selectedPlan.symptoms || 'N/A'}</p>
                            <p><strong>Emergency Treatment Procedures:</strong><br />{selectedPlan.emergency_treatment_procedures || 'N/A'}</p>

                            <hr />

                            <h5 className="mb-3">EpiPen Information:</h5>
                            <p><strong>Requires EpiPen:</strong> {selectedPlan.require_epi_pen === 1 ? 'Yes' : 'No'}</p>
                            {selectedPlan.require_epi_pen === 1 && (
                                <p><strong>Location of EpiPen:</strong> {selectedPlan.location_of_epi_pen || 'N/A'}</p>
                            )}

                            <hr />

                            <h5 className="mb-3">Signatures:</h5>
                            <Row>
                                <Col md="6">
                                    <p><strong>Parent Signature:</strong></p>
                                    {selectedPlan.parent_signature ? (
                                        <div className="border rounded p-2 bg-light mb-2">
                                            <img
                                                src={selectedPlan.parent_signature}
                                                alt="Parent Signature"
                                                style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-muted">N/A</p>
                                    )}
                                    <p><strong>Date:</strong> {formatDate(selectedPlan.parent_signature_date)}</p>
                                </Col>
                                <Col md="6">
                                    <p><strong>Supervisor Signature:</strong></p>
                                    {selectedPlan.supervisor_signature ? (
                                        <div className="border rounded p-2 bg-light mb-2">
                                            <img
                                                src={selectedPlan.supervisor_signature}
                                                alt="Supervisor Signature"
                                                style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-muted">N/A</p>
                                    )}
                                    <p><strong>Date:</strong> {formatDate(selectedPlan.supervisor_signature_date)}</p>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    {selectedPlan && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                handleCloseModal();
                                navigate(`/dashboard/allergy-action-plan/edit/${selectedPlan.id}`);
                            }}
                        >
                            Edit Plan
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};
export default AllergyActionPlanListComp;
